import {
  LayoutDashboard,
  Users,
  DollarSign,
  FileText,
  Briefcase,
  Clock,
  CreditCard,
  ShoppingCart,
  Cloud,
  Package,
  BarChart3,
  Brain,
  Settings,
  Headphones,
  Code,
  User,
  Shield,
  Globe,
  Lock,
  Hash,
  Sliders,
  Menu,
  Layers,
  Building2,
  MessageSquareText,
  PieChart,
  Receipt,
  Mail
} from 'lucide-react'

/** External CRM (Celyntiq) — sidebar opens this in a new tab */
export const CELYNTIQ_CRM_LOGIN_URL = 'https://www.celyntiq.com/login'

/** Main application modules (sidebar when footer = Applications) */
export const APPLICATION_MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  // CRM app lives in Celyntiq — keep menu entry; Sidebar opens externalUrl in a new tab
  { id: 'crm', label: 'CRM', icon: Users, path: '/crm', externalUrl: CELYNTIQ_CRM_LOGIN_URL },
  { id: 'sales', label: 'Sales', icon: DollarSign, path: '/sales' },
  { id: 'contracts', label: 'Contracts', icon: FileText, path: '/contracts' },
  { id: 'projects', label: 'Projects', icon: Briefcase, path: '/projects' },
  { id: 'timesheets', label: 'Timesheets', icon: Clock, path: '/timesheets' },
  { id: 'expenses', label: 'Expenses', icon: CreditCard, path: '/expenses' },
  { id: 'procurement', label: 'Procurement', icon: ShoppingCart, path: '/procurement' },
  { id: 'subscriptions', label: 'Subscriptions', icon: Cloud, path: '/subscriptions' },
  { id: 'assets', label: 'Assets', icon: Package, path: '/assets' },
  { id: 'accounts', label: 'Accounts', icon: BarChart3, path: '/accounts' },
  // Hidden in sidebar (see Sidebar HIDDEN_SIDEBAR_MENU_IDS) — keep for routes/config
  { id: 'development', label: 'Development', icon: Code, path: '/development', hidden: true }
]

/** Cross-module master hubs */
export const MASTERS_MENU_ITEMS = [
  // Hidden — CRM masters are in Celyntiq (see Sidebar HIDDEN_SIDEBAR_MENU_IDS)
  { id: 'crm-masters', label: 'CRM', icon: Users, path: '/crm?tab=masters', hidden: true },
  { id: 'sales-masters', label: 'Sales', icon: DollarSign, path: '/sales?tab=masters' },
  { id: 'contracts-masters', label: 'Contracts', icon: FileText, path: '/contracts?tab=masters' },
  { id: 'projects-masters', label: 'Projects', icon: Briefcase, path: '/projects?tab=masters' },
  { id: 'timesheets-masters', label: 'Timesheets', icon: Clock, path: '/timesheets?tab=masters' },
  { id: 'expenses-masters', label: 'Expenses', icon: CreditCard, path: '/expenses?tab=masters' },
  { id: 'procurement-masters', label: 'Procurement', icon: ShoppingCart, path: '/procurement?tab=masters' },
  { id: 'subscriptions-masters', label: 'Subscriptions', icon: Cloud, path: '/subscriptions?tab=masters' },
  { id: 'assets-masters', label: 'Assets', icon: Package, path: '/assets?tab=masters' },
  { id: 'accounts-masters', label: 'Accounts', icon: BarChart3, path: '/accounts?tab=masters' }
]

/** Cross-module report hubs */
export const REPORTS_MENU_ITEMS = [
  // Hidden — CRM reports are in Celyntiq (see Sidebar HIDDEN_SIDEBAR_MENU_IDS)
  { id: 'crm-reports', label: 'CRM', icon: Users, path: '/crm?tab=reports', hidden: true },
  { id: 'sales-reports', label: 'Sales', icon: DollarSign, path: '/sales?tab=reports' },
  { id: 'contracts-reports', label: 'Contracts', icon: FileText, path: '/contracts?tab=reports' },
  { id: 'projects-reports', label: 'Projects', icon: Briefcase, path: '/projects?tab=reports' },
  { id: 'timesheets-reports', label: 'Timesheets', icon: Clock, path: '/timesheets?tab=reports' },
  { id: 'expenses-reports', label: 'Expenses', icon: CreditCard, path: '/expenses?tab=reports' },
  { id: 'procurement-reports', label: 'Procurement', icon: ShoppingCart, path: '/procurement?tab=reports' },
  { id: 'subscriptions-reports', label: 'Subscriptions', icon: Cloud, path: '/subscriptions?tab=reports' },
  { id: 'assets-reports', label: 'Assets', icon: Package, path: '/assets?tab=reports' },
  { id: 'accounts-reports', label: 'Accounts', icon: BarChart3, path: '/accounts?tab=reports' },
  { id: 'management-reports', label: 'Management', icon: PieChart, path: '/management?tab=reports', activePrefixes: ['/management'] },
  // Hidden in sidebar — keep for routes/config
  { id: 'development-reports', label: 'Development', icon: Code, path: '/development?tab=reports', hidden: true },
  { id: 'billing-reports', label: 'Billing', icon: Receipt, path: '/billing?tab=reports', hidden: true }
]

/**
 * Settings sidebar — hubs only (child screens open from hub cards).
 * Keep leaf paths in activePrefixes so the parent stays highlighted.
 * Leaf entries remain below with hidden:true for route/config references.
 */
export const SETTINGS_MENU_ITEMS = [
  { id: 'settings-home', label: 'Settings', icon: Settings, path: '/settings' },
  {
    id: 'security-master',
    label: 'Security',
    icon: Lock,
    path: '/settings/security-master',
    activePrefixes: [
      '/settings/security-master',
      '/settings/user-master',
      '/settings/role-master',
      '/settings/menu-item-master',
      '/settings/submodule-master',
      '/settings/module-permissions',
    ],
  },
  {
    id: 'organisation',
    label: 'Organisation',
    icon: Building2,
    path: '/settings/organisation',
    activePrefixes: [
      '/settings/organisation',
      '/settings/company-master',
      '/settings/global-master',
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Sliders,
    path: '/settings/system',
    activePrefixes: [
      '/settings/system',
      '/settings/dropdown-master',
      '/settings/auto-increment-master',
      '/settings/application-parameters',
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Mail,
    path: '/settings/notifications',
    activePrefixes: [
      '/settings/notifications',
      '/settings/email-notifications',
      '/settings/gst-reminder-settings',
    ],
  },
  // Leaf screens — hidden from sidebar; reachable via hub cards / direct URL
  { id: 'user-master', label: 'User', icon: User, path: '/settings/user-master', hidden: true },
  { id: 'role-master', label: 'Role', icon: Shield, path: '/settings/role-master', hidden: true },
  { id: 'global-master', label: 'Global', icon: Globe, path: '/settings/global-master', hidden: true },
  { id: 'company-master', label: 'Company', icon: Building2, path: '/settings/company-master', hidden: true },
  { id: 'dropdown-master', label: 'Dropdown', icon: Layers, path: '/settings/dropdown-master', hidden: true },
  { id: 'auto-increment', label: 'Auto Increment', icon: Hash, path: '/settings/auto-increment-master', hidden: true },
  { id: 'menu-item-master', label: 'Menu Items', icon: Menu, path: '/settings/menu-item-master', hidden: true },
  { id: 'submodule-master', label: 'Submodule', icon: Layers, path: '/settings/submodule-master', hidden: true },
  { id: 'module-permissions', label: 'Module Permissions', icon: Shield, path: '/settings/module-permissions', hidden: true },
  { id: 'email-notifications', label: 'Email Notifications', icon: Mail, path: '/settings/email-notifications', hidden: true },
  { id: 'gst-reminder-settings', label: 'GST Reminder & Compliance', icon: Receipt, path: '/settings/gst-reminder-settings', hidden: true },
  { id: 'app-parameters', label: 'App Parameters', icon: Sliders, path: '/settings/application-parameters', hidden: true },
]

export const SUPPORT_MENU_ITEMS = [
  { id: 'support-overview', label: 'Support Overview', icon: Headphones, path: '/support?view=overview' },
  { id: 'support-masters', label: 'Support Masters', icon: Users, path: '/support?tab=masters' },
  { id: 'support-transactions', label: 'Support Transactions', icon: FileText, path: '/support?tab=transactions' },
  { id: 'support-tickets', label: 'Support Tickets', icon: MessageSquareText, path: '/support/support-tickets' }
]

export const FOOTER_NAV_MODES = {
  applications: {
    id: 'applications',
    label: 'App',
    defaultPath: '/dashboard',
    menuItems: APPLICATION_MENU_ITEMS
  },
  masters: {
    id: 'masters',
    label: 'Masters',
    defaultPath: '/sales?tab=masters',
    menuItems: MASTERS_MENU_ITEMS
  },
  reports: {
    id: 'reports',
    label: 'Reports',
    defaultPath: '/sales?tab=reports',
    menuItems: REPORTS_MENU_ITEMS
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    defaultPath: '/settings',
    menuItems: SETTINGS_MENU_ITEMS
  },
  support: {
    id: 'support',
    label: 'Support',
    defaultPath: '/support?tab=transactions',
    menuItems: SUPPORT_MENU_ITEMS
  }
}

const SETTINGS_PATH_PREFIXES = [
  '/settings'
]

/** Infer active footer section from current URL */
export function resolveFooterNavMode(pathname, search = '') {
  const path = pathname.toLowerCase()
  const qs = search.toLowerCase()

  if (path.startsWith('/support')) {
    return 'support'
  }

  if (qs.includes('tab=reports') || path.startsWith('/management')) {
    return 'reports'
  }

  if (SETTINGS_PATH_PREFIXES.some((p) => path.startsWith(p))) {
    return 'settings'
  }

  if (qs.includes('tab=masters') || path.includes('-master')) {
    return 'masters'
  }

  return 'applications'
}
